/* Shopify cart mutations and server-rendered sections share one request queue. */
(() => {
  'use strict';
  if (window.HomiHavenCartDrawer || typeof HTMLDialogElement === 'undefined') return;
  const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
  let drawer, trigger, queue = Promise.resolve(), pending = 0, closeTimer;
  const cartPage = () => document.querySelector('[data-cart-page-section]');
  const sectionIds = () => [drawer.dataset.sectionId, cartPage()?.dataset.cartPageSection].filter(Boolean);
  const status = message => { drawer.querySelector('[data-cart-drawer-status]').textContent = message; };

  function setBusy() {
    drawer.setAttribute('aria-busy', String(pending > 0));
    const fields = drawer.querySelector('[data-cart-drawer-fields]');
    if (fields) fields.disabled = pending > 0;
  }
  function enqueue(task) {
    pending++;
    setBusy();
    const job = queue.then(task).finally(() => { pending--; setBusy(); });
    queue = job.catch(() => {});
    return job;
  }
  function error(message, hideContent = false) {
    drawer.querySelector('[data-cart-drawer-error-text]').textContent = message;
    drawer.querySelector('[data-cart-drawer-error]').hidden = false;
    if (hideContent) drawer.querySelector('[data-cart-drawer-content]').hidden = true;
  }
  function clearError() { drawer.querySelector('[data-cart-drawer-error]').hidden = true; }
  function open(source) {
    clearTimeout(closeTimer);
    drawer.classList.remove('is-closing');
    if (!drawer.open) {
      trigger = source || document.activeElement;
      drawer.showModal();
      document.body.classList.add('cart-drawer-open');
      drawer.querySelector('[data-cart-close]').focus({ preventScroll: true });
    }
    document.querySelectorAll('[data-cart-open], .header-cart').forEach(link => link.setAttribute('aria-expanded', 'true'));
  }
  function close() {
    if (!drawer.open) return;
    drawer.classList.add('is-closing');
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => drawer.close(), matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200);
  }
  function closed() {
    document.body.classList.remove('cart-drawer-open');
    drawer.classList.remove('is-closing');
    document.querySelectorAll('[data-cart-open], .header-cart').forEach(link => link.setAttribute('aria-expanded', 'false'));
    const target = trigger?.isConnected && !trigger.disabled ? trigger : document.querySelector('[data-cart-open], .header-cart');
    target?.focus({ preventScroll: true });
  }
  function render(sections) {
    const source = sections?.[drawer.dataset.sectionId];
    if (!source) throw new Error('Cart section unavailable');
    const documentFragment = new DOMParser().parseFromString(source, 'text/html');
    const content = documentFragment.querySelector('[data-cart-drawer-content]');
    const cart = JSON.parse(content?.querySelector('[data-cart-drawer-state]')?.textContent || 'null');
    if (!content || !cart || !Number.isFinite(cart.item_count)) throw new Error('Cart section unavailable');
    const scrollTop = drawer.querySelector('.cart-drawer__body')?.scrollTop || 0;
    drawer.querySelector('[data-cart-drawer-content]').replaceWith(document.importNode(content, true));
    const scroller = drawer.querySelector('.cart-drawer__body');
    if (scroller) scroller.scrollTop = scrollTop;
    drawer.querySelector('[data-drawer-count]').textContent = cart.item_count;
    document.querySelectorAll('[data-cart-count]').forEach(badge => {
      badge.textContent = cart.item_count;
      badge.classList.toggle('is-empty', cart.item_count === 0);
    });
    const currentPage = cartPage();
    const pageHtml = currentPage && sections[currentPage.dataset.cartPageSection];
    if (pageHtml) {
      const replacement = new DOMParser().parseFromString(pageHtml, 'text/html').querySelector('[data-cart-page-section]');
      if (replacement) {
        const note = currentPage.querySelector('[name="note"]');
        if (note && note.value !== note.defaultValue) {
          const newNote = replacement.querySelector('[name="note"]');
          if (newNote) newNote.value = note.value;
        }
        currentPage.replaceWith(document.importNode(replacement, true));
      }
    }
    clearError();
    setBusy();
    status(`Cart updated. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}.`);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }
  async function refresh() {
    const url = new URL(location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', sectionIds().join(','));
    const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Cart refresh failed');
    render(await response.json());
  }
  async function applyResult(result) {
    try { render(result.sections); }
    catch (_) { await refresh(); }
  }
  async function readResult(response) {
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(typeof result.description === 'string' ? result.description : (result.message || 'Your cart could not be updated. Please try again.'));
    return result;
  }
  async function add(formData, source) {
    return enqueue(async () => {
      formData.set('sections', sectionIds().join(','));
      formData.set('sections_url', location.pathname);
      const result = await readResult(await fetch(config.cartAddUrl || '/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: formData }));
      open(source);
      try { await applyResult(result); }
      catch (_) { error('Your item was added, but the cart preview could not load. View your cart to continue.', true); }
      return result;
    });
  }
  async function change(line, quantity, source) {
    const key = line.dataset.key;
    const focusControl = source.dataset.cartFocus || 'quantity';
    await enqueue(async () => {
      clearError();
      status('Updating your cart…');
      let changed = false;
      try {
        const response = await fetch(config.cartChangeUrl || '/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: location.pathname })
        });
        const result = await readResult(response);
        changed = true;
        await applyResult(result);
      } catch (failure) {
        // Never repeat a mutation after an uncertain network response.
        let refreshed = false;
        try { await refresh(); refreshed = true; } catch (_) { /* Keep a native cart recovery link. */ }
        const message = changed ? 'Your cart was updated, but the preview could not load. View your cart to continue.'
          : failure instanceof TypeError ? 'Connection interrupted. Check your cart before trying again.' : failure.message;
        error(message, !refreshed);
      }
    });
    if (!drawer.open) return;
    const remainingLine = [...drawer.querySelectorAll('[data-cart-line]')].find(item => item.dataset.key === key);
    const control = remainingLine?.querySelector(`[data-cart-focus="${focusControl}"]`);
    (control && !control.disabled ? control : drawer.querySelector('[data-cart-close]')).focus({ preventScroll: true });
  }
  function validateQuantity(input) {
    const quantity = Number(input.value);
    if (input.value.trim() !== '' && Number.isInteger(quantity) && (quantity === 0 || input.checkValidity())) return quantity;
    error('Enter a quantity within the available limits.');
    input.reportValidity();
    input.value = input.dataset.currentQuantity;
    return null;
  }
  function init() {
    const current = document.querySelector('[data-cart-drawer]');
    if (!current) return;
    if (drawer !== current) {
      if (drawer?.open) { clearTimeout(closeTimer); drawer.close(); closed(); }
      drawer = current;
      drawer.addEventListener('cancel', event => { event.preventDefault(); close(); });
      drawer.addEventListener('close', closed);
      drawer.addEventListener('click', event => {
        if (event.target !== drawer) return;
        const bounds = drawer.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
      });
    }
    document.querySelectorAll('[data-cart-open], .header-cart').forEach(link => {
      link.setAttribute('aria-controls', 'CartDrawer');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-expanded', String(drawer.open));
    });
  }
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-cart-open], .header-cart');
    if (opener && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      open(opener);
      enqueue(async () => {
        clearError(); status('Loading your cart…');
        try { await refresh(); }
        catch (_) { error('Your cart could not load. View your cart or close this panel and try again.', true); }
      });
      return;
    }
    if (!event.target.closest('[data-cart-drawer]')) return;
    if (event.target.closest('[data-cart-close]')) { event.preventDefault(); close(); return; }
    if (pending) return;
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { change(remove.closest('[data-cart-line]'), 0, remove); return; }
    const step = event.target.closest('[data-cart-step]');
    if (step) {
      const line = step.closest('[data-cart-line]');
      const input = line.querySelector('[data-cart-quantity]');
      Number(step.dataset.cartStep) > 0 ? input.stepUp() : input.stepDown();
      const quantity = validateQuantity(input);
      if (quantity !== null && quantity !== Number(input.dataset.currentQuantity)) change(line, quantity, step);
    }
  });
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input || pending) return;
    const quantity = validateQuantity(input);
    if (quantity !== null && quantity !== Number(input.dataset.currentQuantity)) change(input.closest('[data-cart-line]'), quantity, input);
  });
  document.addEventListener('keydown', event => {
    if (drawer?.open && event.key === 'Tab') {
      const focusable = [...drawer.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[hidden]') && element.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
        event.preventDefault(); first?.focus();
      }
    }
    if (event.key === 'Enter' && event.target.matches('[data-cart-quantity]')) {
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  document.addEventListener('submit', event => {
    if (event.target.id === 'CartDrawerForm' && pending) event.preventDefault();
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', event => {
    if (event.target.contains(drawer)) open(document.querySelector('[data-cart-open], .header-cart'));
  });
  document.addEventListener('shopify:section:deselect', event => { if (event.target.contains(drawer)) close(); });
  window.addEventListener('pageshow', event => {
    if (event.persisted) enqueue(async () => { try { await refresh(); } catch (_) { /* Refresh on the next cart open. */ } });
  });
  init();
  if (drawer) window.HomiHavenCartDrawer = { add };
})();
